import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { faqPreviewUrl, type FaqItem } from "@/pages/help/faqData";
import {
  diffContentFields,
  hasDraft,
  hasLiveOverride,
  liveContent,
  payloadToContent,
  type FaqContentOverride,
  type FaqContentPayload,
  type FaqVersionContent,
  type FaqVersionRow,
  type FaqContentField,
} from "@/pages/help/faqContent";
import FaqVersionHistory from "@/pages/admin/help/components/FaqVersionHistory";

interface FaqContentEditorProps {
  item: FaqItem;
  override?: FaqContentOverride;
  saving: boolean;
  versions: FaqVersionRow[];
  versionsLoading: boolean;
  onClose: () => void;
  onSaveDraft: (payload: FaqContentPayload) => void;
  onPublish: (payload: FaqContentPayload) => void;
  onReset: () => void;
  onRollback: (version: FaqVersionRow) => void;
}

type EditorTab = "edit" | "history";

const textareaClass =
  "w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400";

const FIELD_TO_FORM: Record<FaqContentField, keyof FaqContentPayload> = {
  question_zh: "questionZh",
  answer_zh: "answerZh",
  question_en: "questionEn",
  answer_en: "answerEn",
};

export default function FaqContentEditor({
  item,
  override,
  saving,
  versions,
  versionsLoading,
  onClose,
  onSaveDraft,
  onPublish,
  onReset,
  onRollback,
}: FaqContentEditorProps) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<EditorTab>("edit");

  const initial = useMemo(() => {
    const source = hasDraft(override)
      ? {
          question_zh: override?.draft_question_zh ?? null,
          answer_zh: override?.draft_answer_zh ?? null,
          question_en: override?.draft_question_en ?? null,
          answer_en: override?.draft_answer_en ?? null,
        }
      : liveContent(override);
    return {
      questionZh: source.question_zh ?? "",
      answerZh: source.answer_zh ?? "",
      questionEn: source.question_en ?? "",
      answerEn: source.answer_en ?? "",
    };
  }, [override]);

  const [form, setForm] = useState<FaqContentPayload>(initial);

  const defaultZhQ = String(i18n.t(item.qKey, { lng: "zh" }));
  const defaultZhA = String(i18n.t(item.aKey, { lng: "zh" }));
  const defaultEnQ = String(i18n.t(item.qKey, { lng: "en" }));
  const defaultEnA = String(i18n.t(item.aKey, { lng: "en" }));

  const defaults: FaqVersionContent = {
    question_zh: defaultZhQ,
    answer_zh: defaultZhA,
    question_en: defaultEnQ,
    answer_en: defaultEnA,
  };

  const payload = (): FaqContentPayload => form;
  const draftExists = hasDraft(override);
  const editedBefore = hasLiveOverride(override);

  const changedFields = useMemo<FaqContentField[]>(
    () => diffContentFields(liveContent(override), payloadToContent(form)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form, override],
  );

  const liveOr = (field: FaqContentField): string => {
    const live = liveContent(override)[field];
    return live && live.trim() ? live : defaults[field] ?? "";
  };
  const draftOr = (field: FaqContentField): string => {
    const value = payloadToContent(form)[field];
    return value && value.trim() ? value : defaults[field] ?? "";
  };

  const fieldLabel = (field: FaqContentField) => {
    const lang =
      field.endsWith("_zh")
        ? t("helpFeedback.contentLangZh")
        : t("helpFeedback.contentLangEn");
    const label = field.startsWith("question")
      ? t("helpFeedback.contentQuestion")
      : t("helpFeedback.contentAnswer");
    return `${lang} · ${label}`;
  };

  const formField = (formKey: keyof FaqContentPayload) => ({
    value: form[formKey],
    onChange: (event: { target: { value: string } }) =>
      setForm((prev) => ({ ...prev, [formKey]: event.target.value })),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6">
      <div className="flex max-h-full w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="flex items-start justify-between gap-4 border-b border-background-200 px-5 py-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-heading text-base font-semibold text-foreground-950">{t("helpFeedback.editTitle")}</h3>
              {draftExists && (
                <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-medium text-secondary-900">
                  <span className="flex h-3 w-3 items-center justify-center">
                    <i className="ri-draft-line" />
                  </span>
                  {t("helpFeedback.draftBadge")}
                </span>
              )}
              {editedBefore && (
                <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-background-200 px-2 py-0.5 text-[11px] text-foreground-700">
                  <span className="flex h-3 w-3 items-center justify-center">
                    <i className="ri-edit-2-line" />
                  </span>
                  {t("helpFeedback.contentEditedBadge")}
                </span>
              )}
            </div>
            <p className="mt-1 text-xs leading-relaxed text-foreground-500">{t("helpFeedback.editSub")}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("helpFeedback.contentCancel")}
            className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-900"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="flex items-center gap-1.5 border-b border-background-200 px-5 py-2.5">
          <button
            type="button"
            onClick={() => setTab("edit")}
            className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
              tab === "edit" ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:bg-background-100"
            }`}
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-edit-box-line" />
            </span>
            {t("helpFeedback.tabEdit")}
          </button>
          <button
            type="button"
            onClick={() => setTab("history")}
            className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
              tab === "history" ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:bg-background-100"
            }`}
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-history-line" />
            </span>
            {t("helpFeedback.tabHistory")}
            {versions.length > 0 && (
              <span
                className={`flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-medium ${
                  tab === "history" ? "bg-background-50/25 text-background-50" : "bg-background-200 text-foreground-600"
                }`}
              >
                {versions.length}
              </span>
            )}
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
          {tab === "history" ? (
            <FaqVersionHistory
              versions={versions}
              loading={versionsLoading}
              saving={saving}
              questionLabel={t(item.qKey)}
              onRollback={onRollback}
            />
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <a
                  href={faqPreviewUrl(item.qKey)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-300"
                >
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className="ri-external-link-line" />
                  </span>
                  {t("helpFeedback.contentPreview")}
                </a>
                <span className="text-xs text-foreground-500">{t("helpFeedback.contentFieldEmpty")}</span>
              </div>

              <section className="rounded-lg border border-background-200 bg-background-100/60 px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-secondary-100 text-secondary-900">
                    <i className="ri-arrow-left-right-line text-xs" />
                  </span>
                  <h4 className="text-sm font-medium text-foreground-950">{t("helpFeedback.diffTitle")}</h4>
                  <span className="text-[11px] text-foreground-500">
                    {changedFields.length > 0
                      ? t("helpFeedback.diffCount", { count: changedFields.length })
                      : t("helpFeedback.diffNoChange")}
                  </span>
                </div>
                {changedFields.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {changedFields.map((field) => (
                      <div key={field} className="rounded-md border border-background-200 bg-background-50 px-3 py-2">
                        <p className="text-[11px] font-medium text-foreground-700">{fieldLabel(field)}</p>
                        <div className="mt-1.5 grid gap-1.5 sm:grid-cols-2">
                          <div className="rounded-md bg-accent-50 px-2.5 py-1.5">
                            <span className="text-[10px] uppercase tracking-wide text-accent-700">
                              {t("helpFeedback.diffBefore")}
                            </span>
                            <p className="mt-0.5 break-words text-xs leading-relaxed text-foreground-700 line-through decoration-foreground-400/60">
                              {liveOr(field)}
                            </p>
                          </div>
                          <div className="rounded-md bg-primary-50 px-2.5 py-1.5">
                            <span className="text-[10px] uppercase tracking-wide text-primary-700">
                              {t("helpFeedback.diffAfter")}
                            </span>
                            <p className="mt-0.5 break-words text-xs leading-relaxed text-foreground-900">
                              {draftOr(field)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-secondary-100 text-secondary-900">
                    <i className="ri-translate-2 text-xs" />
                  </span>
                  <h4 className="text-sm font-medium text-foreground-950">{t("helpFeedback.contentLangZh")}</h4>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-foreground-700">
                    {t("helpFeedback.contentQuestion")}
                  </label>
                  <textarea rows={2} placeholder={defaultZhQ} className={textareaClass} {...formField("questionZh")} />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-foreground-700">
                    {t("helpFeedback.contentAnswer")}
                  </label>
                  <textarea rows={4} placeholder={defaultZhA} className={textareaClass} {...formField("answerZh")} />
                </div>
              </section>

              <section className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-secondary-100 text-secondary-900">
                    <i className="ri-english-input text-xs" />
                  </span>
                  <h4 className="text-sm font-medium text-foreground-950">{t("helpFeedback.contentLangEn")}</h4>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-foreground-700">
                    {t("helpFeedback.contentQuestion")}
                  </label>
                  <textarea rows={2} placeholder={defaultEnQ} className={textareaClass} {...formField("questionEn")} />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-foreground-700">
                    {t("helpFeedback.contentAnswer")}
                  </label>
                  <textarea rows={4} placeholder={defaultEnA} className={textareaClass} {...formField("answerEn")} />
                </div>
              </section>
            </>
          )}
        </div>

        <div className="flex flex-col gap-3 border-t border-background-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={onReset}
            disabled={saving || !editedBefore}
            className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-xs text-foreground-700 transition-colors hover:border-accent-300 hover:text-accent-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-arrow-go-back-line" />
            </span>
            {t("helpFeedback.contentReset")}
          </button>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-800 transition-colors hover:bg-background-100"
            >
              {t("helpFeedback.contentCancel")}
            </button>
            <button
              type="button"
              onClick={() => onSaveDraft(payload())}
              disabled={saving}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-secondary-300 bg-secondary-50 px-4 py-2 text-sm font-medium text-secondary-900 transition-colors hover:bg-secondary-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-draft-line" />
              </span>
              {t("helpFeedback.saveDraft")}
            </button>
            <button
              type="button"
              onClick={() => onPublish(payload())}
              disabled={saving}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className={saving ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
              </span>
              {saving ? t("helpFeedback.contentSaving") : t("helpFeedback.publish")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}